import type { SVGProps } from 'react';

import { cn } from '@shared/ui/cn';

/**
 * Inline SVG components. No icon font, no remote icon CDN, no third-party
 * origin of any kind.
 *
 * Every glyph is drawn on the design system's 24×24 grid at
 * `stroke-width: 2`, `stroke-linecap: round`, in `currentColor`. The set is a
 * **style contract, not a closed inventory**: a new icon is drawn on the same
 * grid rather than borrowed from another family.
 *
 * Decorative by default (`aria-hidden`); a meaning-bearing icon is given a
 * `role="img"` and a title by its caller, and an icon-only button always
 * carries its own `aria-label`.
 *
 * Directional icons mirror under `[dir="rtl"]` — the `rtl:-scale-x-100` class
 * on the glyph, never a second file. A cog, a bank card and a bar chart have
 * no reading direction and are not mirrored.
 */

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1.125em"
      height="1.125em"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Icon>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Icon>
  );
}

export function SunIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.5 1.5M17.6 17.6l1.5 1.5M19.1 4.9l-1.5 1.5M6.4 17.6l-1.5 1.5" />
    </Icon>
  );
}

export function MoonIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 14.5A8.5 8.5 0 019.5 4a8.5 8.5 0 1010.5 10.5z" />
    </Icon>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </Icon>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6M12 7.5v.5" />
    </Icon>
  );
}

export function WarningIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17v.5" />
    </Icon>
  );
}

export function DangerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </Icon>
  );
}

/**
 * The glyph beside a field-level error. A circle rather than the alert
 * triangle: the triangle is the banner's mark, and reusing it under a field
 * would make one input read as loud as a whole form failing.
 *
 * It ships because the design system's `.field__error` is icon **and** text —
 * a red sentence with no mark carries its state in colour alone.
 */
export function FieldErrorIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16.2v.2" />
    </Icon>
  );
}

/**
 * The envelope on the sign-in screen's primary action — an SMS is what actually
 * arrives, and no glyph in this set says «short message» without a device in it.
 *
 * An envelope has no reading direction: the flap is drawn on the block axis, so
 * it is **not** mirrored under `[dir="rtl"]`.
 */
export function MailIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5h16v14H4z" />
      <path d="M4 6.5l8 5.5 8-5.5" />
    </Icon>
  );
}

/**
 * A link that leaves this host — the box with an arrow out of it.
 *
 * **Not mirrored.** The arrow means «away from here», not «forward», and the
 * UI/UX pack draws this exact path under `dir="rtl"` without flipping it; a
 * mirroring class on top of a path authored for the reading direction turns the
 * arrow back into the box.
 */
export function ExternalLinkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14 4h6v6" />
      <path d="M20 4l-8.5 8.5" />
      <path d="M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5" />
    </Icon>
  );
}

export function CopyIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a1 1 0 011-1h9" />
    </Icon>
  );
}

/** Block — a barred circle. The bar is a slash, not an arrow, so it is not mirrored. */
export function BanIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M6 6l12 12" />
    </Icon>
  );
}

/** Unblock — the same circle with the check that lifts the bar. */
export function UnbanIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.4 12.2l2.4 2.4 4.8-5.2" />
    </Icon>
  );
}

/** The account register's empty state — a person the search did not find. */
export function UserOffIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="10" cy="7.8" r="3.8" />
      <path d="M3 20a7 7 0 0111-5.7" />
      <path d="M16 16l5 5M21 16l-5 5" />
    </Icon>
  );
}

/**
 * The row-menu handle — three dots on the block axis.
 *
 * It carries no reading direction, so it is not mirrored; the panel it opens is
 * what has to know which edge it grows from, and that arithmetic lives in
 * `@shared/ui/rowmenu/`.
 */
export function MoreIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="5" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="12" cy="19" r="1.4" />
    </Icon>
  );
}

/** Edit — a pencil over its own stroke. Transcribed from the UI/UX pack. */
export function EditIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 20h4l10-10a2.1 2.1 0 10-3-3L5 17z" />
      <path d="M13.5 6.5l3 3" />
    </Icon>
  );
}

/** Delete — a bin. The only glyph in a row menu that takes the danger colour. */
export function TrashIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M5 7h14M10 7V5.5a1 1 0 011-1h2a1 1 0 011 1V7" />
      <path d="M6.5 7l.8 12a1 1 0 001 .9h7.4a1 1 0 001-.9l.8-12" />
    </Icon>
  );
}

/**
 * Add a child under this node — a branch turning down into a `+`. Drawn for
 * `rtl` in the pack's own path, so it carries no mirroring class: a transform on
 * top of a path authored for the reading direction flips it back.
 */
export function AddChildIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M17 4v9a3 3 0 01-3 3h-3" />
      <path d="M7 13v6M4 16h6" />
    </Icon>
  );
}

/**
 * The add action beside a section head. Re-exported rather than re-drawn: the
 * glyph the top bar's create action uses is the same one, and a second copy of
 * `M12 5v14M5 12h14` is a literal repeated at two call sites.
 */
export { PlusIcon } from './nav';

/**
 * The two reorder handles. **Not mirrored** — up and down are the block axis of
 * an ordered list, and the block axis does not reverse with reading direction.
 * Mirroring them would be the §10.2 error in the other plane.
 */
export function ArrowUpIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 19V5M6 11l6-6 6 6" />
    </Icon>
  );
}

export function ArrowDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </Icon>
  );
}

/**
 * The disclosure chevron a list control turns to say it is open.
 *
 * **Not mirrored**, for the reason the two reorder handles are not: it turns
 * through the block axis, and the block axis does not reverse with reading
 * direction. The caller rotates it; the glyph itself points down.
 */
export function ChevronDownIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m6 9 6 6 6-6" />
    </Icon>
  );
}

/**
 * The two traversal chevrons a pager walks with.
 *
 * **Both mirror**, unlike the disclosure chevron above: these turn through the
 * *inline* axis, and the inline axis is exactly what reading direction
 * reverses. Under `dir="rtl"` «previous» therefore points at the right-hand
 * edge and «next» at the left-hand one, which is where an Arabic reader's
 * previous and next actually are. The mirroring is a class on the glyph rather
 * than a second pair of files, and it is merged with any the caller passes so a
 * call site can still size or colour the icon.
 */
export function ChevronPreviousIcon({ className, ...props }: IconProps) {
  return (
    <Icon className={cn('rtl:-scale-x-100', className)} {...props}>
      <path d="m15 6-6 6 6 6" />
    </Icon>
  );
}

export function ChevronNextIcon({ className, ...props }: IconProps) {
  return (
    <Icon className={cn('rtl:-scale-x-100', className)} {...props}>
      <path d="m9 6 6 6-6 6" />
    </Icon>
  );
}

/**
 * A broken ring — a link that no longer arrives. The not-found state's mark on
 * all three surfaces of the platform: the state is identical everywhere, so a
 * second shape for it would be variation without a difference.
 */
export function BrokenLinkIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M10 7.5H7.5a4.5 4.5 0 0 0 0 9H10" />
      <path d="M14 7.5h2.5a4.5 4.5 0 0 1 0 9H14" />
      <path d="M9.6 12h1.2M13.2 12h1.2" />
    </Icon>
  );
}

/**
 * A circle carrying an exclamation — a fault, not a warning. It is distinct from
 * `DangerIcon`, whose cross reads «this was rejected»: a service that did not
 * answer refused nothing.
 */
export function ServerFaultIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5" />
      <path d="M12 16.4v.1" />
    </Icon>
  );
}

/**
 * The retry arc. It is **not** mirrored under `[dir="rtl"]`: a refresh is a
 * repetition rather than a direction in reading, and flipping it would say
 * «undo» instead.
 */
export function RetryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4v4h-4" />
    </Icon>
  );
}
